import { EnumUtil } from '@csp/csp-common-enum-util';
import {
  CspError,
  Maybe,
  PatientAppStatusType,
  PatientStatusType,
  PatientStudyStatusType,
  StateAssert,
  TimeStr,
} from '@csp/csp-common-model';
import { ObjectUtil } from '@csp/csp-common-util';
import { RequestCriticalityConfigMapper } from '../criticality-config/mapper/RequesCriticalityConfigMapper';
import { RequestInitiationConfigMapper } from '../initiation-config/mapper/RequestInitiationConfigMapper';
import { GenericFixedActionV1 } from '../model/__internal__/GenericFixedActionV1';
import { GenericRequestActionV1 } from '../model/__internal__/GenericRequestActionV1';
import { GenericRequestDurationV1 } from '../model/__internal__/GenericRequestDurationV1';
import { GenericRequestRepeatV1 } from '../model/__internal__/GenericRequestRepeatV1';
import { GenericRequestTimingV1 } from '../model/__internal__/GenericRequestTimingV1';
import { GenericRequestUnitOfTimeV1 } from '../model/__internal__/GenericRequestUnitOfTimeV1';
import { GenericRequestV1 } from '../model/__internal__/GenericRequestV1';
import { GenericWhileV2 } from '../model/__internal__/GenericWhileV2';
import { ScheduleCode } from '../model/codes/ScheduleCode';
import { VersionCode } from '../model/codes/VersionCode';
import { CustomAction } from '../model/schedulingModels/CustomAction';
import { DayOfWeek } from '../model/schedulingModels/DayOfWeek';
import { Duration } from '../model/schedulingModels/Duration';
import { FixedAction } from '../model/schedulingModels/FixedAction';
import { FixedActionType } from '../model/schedulingModels/FixedActionType';
import { GenericRequest } from '../model/schedulingModels/GenericRequest';
import { Repeat } from '../model/schedulingModels/Repeat';
import { RequestAction } from '../model/schedulingModels/RequestAction';
import { RequestWhile } from '../model/schedulingModels/RequestWhile';
import { RequestWhileCriterionType } from '../model/schedulingModels/RequestWhileCriterionType';
import { RequestWhileOperator } from '../model/schedulingModels/RequestWhileOperator';
import { Timing } from '../model/schedulingModels/Timing';
import { TimingRepeatType } from '../model/schedulingModels/TimingRepeatType';
import { UnitOfTime } from '../model/schedulingModels/UnitOfTime';
import { ReminderNotificationMetaMapper } from '../notification-config/mapper/ReminderNotificationMetaMapper';
import { CustomActionDateType } from '../request-action/model/custom-action/CustomActionDateType';
import {
  CustomActionKeyAcceptedValues,
  CustomActionMetaKeys,
} from '../request-action/model/custom-action/CustomActionMeta';
import { RequestGroupMapper } from '../request-group/mapper/RequestGroupMapper';
import { RpmEventConfigMapper } from '../rpm-event-config/mapper/RpmEventConfigMapper';
import { RequestTagsConfigMetaMapper } from '../tags-configuration/mapper/RequestTagsConfigMetaMapper';

const ALL_UNIT_OF_TIMES = EnumUtil.asArray<UnitOfTime>(UnitOfTime);

const getDefaultWindowBefore = (repeat: Repeat): Duration => {
  switch (repeat.repeatType) {
    case TimingRepeatType.PERIOD:
      return {
        value: 0,
        unit: repeat?.periodUnit ?? UnitOfTime.D,
      };
    case TimingRepeatType.DAILY:
      return {
        unit: UnitOfTime.D,
        value: 0,
      };
    case TimingRepeatType.NONE:
      return {
        unit: UnitOfTime.D,
        value: 0,
      };
  }
};
const getDefaultWindowAfter = (repeat: Repeat): Maybe<Duration> => {
  switch (repeat.repeatType) {
    case TimingRepeatType.PERIOD:
      return {
        value: 0,
        unit: repeat?.periodUnit ?? UnitOfTime.D,
      };
    case TimingRepeatType.DAILY:
      return {
        unit: UnitOfTime.D,
        value: 0,
      };
    case TimingRepeatType.NONE:
      return undefined;
  }
};

const customActionDateTypeToFixedAction = (customActionDateType: CustomActionDateType): Maybe<FixedAction> => {
  if (customActionDateType === 'ACTIVATION') {
    return {
      type: FixedActionType.USER_CUSTOM_STATUS_SET,
      subType: PatientStatusType.APP,
      value: PatientAppStatusType.ACTIVE,
    };
  } else if (customActionDateType === 'RANDOMIZATION') {
    return {
      type: FixedActionType.USER_CUSTOM_STATUS_SET,
      subType: PatientStatusType.STUDY,
      value: PatientStudyStatusType.RANDOMIZED,
    };
  } else if (customActionDateType === 'SCREENING') {
    return {
      type: FixedActionType.USER_CUSTOM_STATUS_SET,
      subType: PatientStatusType.STUDY,
      value: PatientStudyStatusType.SCREENED,
    };
  } else {
    return undefined;
  }
};

const getCustomActionDateType = (customAction: CustomAction): CustomActionDateType => {
  if (customAction.meta) {
    switch (customAction.meta[CustomActionMetaKeys.DATE]) {
      case CustomActionKeyAcceptedValues.DATE.RANDOMIZATION: {
        return 'RANDOMIZATION';
      }
      case CustomActionKeyAcceptedValues.DATE.SCREENING: {
        return 'SCREENING';
      }
      case CustomActionKeyAcceptedValues.DATE.ACTIVATION: {
        return 'ACTIVATION';
      }
      default: {
        return 'UNKNOWN';
      }
    }
  } else {
    return 'UNKNOWN';
  }
};

const customActionTimeToFixedTime = (customAction: CustomAction): Maybe<TimeStr> => {
  if (customAction.meta) {
    const timeMetaProp = customAction.meta[CustomActionMetaKeys.TIME];
    return timeMetaProp !== undefined ? (timeMetaProp as string) : undefined;
  } else {
    return undefined;
  }
};

const customActionDateToFixedAction = (customAction: CustomAction): Maybe<FixedAction> => {
  const customActionDateType = getCustomActionDateType(customAction);
  return customActionDateTypeToFixedAction(customActionDateType);
};

const toRequestWhile = (requestWhileV2: Maybe<GenericWhileV2>): Maybe<RequestWhile> =>
  requestWhileV2?.criteria
    ? {
        criteria: requestWhileV2.criteria.map(criteriaV2 => {
          StateAssert.notNull(
            criteriaV2.type,
            `While criteria type must be specified. while config: ${JSON.stringify(requestWhileV2)}`,
          );
          StateAssert.notNull(
            criteriaV2.value,
            `While criteria value must be specified. while config: ${JSON.stringify(requestWhileV2)}`,
          );
          StateAssert.notNull(
            criteriaV2.subType,
            `While criteria subType must be specified. while config: ${JSON.stringify(requestWhileV2)}`,
          );
          return {
            operator: EnumUtil.fromMaybeStringOrDefault(
              criteriaV2.operator,
              RequestWhileOperator,
              RequestWhileOperator.IN,
            ),
            type: EnumUtil.fromStringOrError(criteriaV2.type, RequestWhileCriterionType),
            subType: criteriaV2.subType,
            values: criteriaV2.value,
          };
        }),
      }
    : undefined;

const toFixedAction = (requestFixedActionV2: Maybe<GenericFixedActionV1>): Maybe<FixedAction> => {
  if (requestFixedActionV2) {
    StateAssert.notNull(requestFixedActionV2.subType, 'Request fixedAction must have a subType specified');
    StateAssert.notNull(requestFixedActionV2.value, 'Request fixedAction must have a value specified');
    return {
      type: EnumUtil.fromStringOrError(requestFixedActionV2.type, FixedActionType),
      subType: requestFixedActionV2.subType,
      value: requestFixedActionV2.value,
    };
  } else {
    return undefined;
  }
};

const isRepeatingWithTimesOfDayOrDaysOfWeek = (repeat: Maybe<GenericRequestRepeatV1>): boolean => {
  const daysOfWeek = repeat?.daysOfWeek;
  const timesOfDay = repeat?.timesOfDay;
  return (!!timesOfDay && timesOfDay?.length > 0) || (!!daysOfWeek && daysOfWeek.length > 0);
};
const isRepeatingWithPeriod = (repeat: Maybe<GenericRequestRepeatV1>): boolean => {
  const period = repeat?.period;
  return period !== undefined && period > 0;
};
const getRepeatType = (repeat: Maybe<GenericRequestRepeatV1>): TimingRepeatType => {
  if (isRepeatingWithTimesOfDayOrDaysOfWeek(repeat)) {
    return TimingRepeatType.DAILY;
  } else if (isRepeatingWithPeriod(repeat)) {
    return TimingRepeatType.PERIOD;
  } else {
    return TimingRepeatType.NONE;
  }
};

const toUnitOfTimeInternal = (unitOfTimeV1: GenericRequestUnitOfTimeV1): UnitOfTime => {
  switch (unitOfTimeV1) {
    case 'S':
      return UnitOfTime.S;
    case 'MIN':
      return UnitOfTime.MIN;
    case 'H':
      return UnitOfTime.H;
    case 'D':
      return UnitOfTime.D;
    case 'WK':
      return UnitOfTime.WK;
    case 'MO':
      return UnitOfTime.MO;
    case 'A':
      return UnitOfTime.A;
  }
};

const toUnitOfTime = (unitOfTimeV1: GenericRequestUnitOfTimeV1, supportedUnitOfTimes: UnitOfTime[]): UnitOfTime => {
  const unitOfTime = toUnitOfTimeInternal(unitOfTimeV1);
  if (supportedUnitOfTimes.includes(unitOfTime)) {
    return unitOfTime;
  } else {
    throw CspError.badState(
      `Unsupported unit of time '${unitOfTimeV1}'. Must be one of: ${supportedUnitOfTimes.join(', ')}`,
    );
  }
};

const toDuration = (
  { unit, value }: GenericRequestDurationV1,
  supportedUnitOfTimes: Maybe<UnitOfTime[]>,
): Duration => ({
  unit: toUnitOfTime(unit, supportedUnitOfTimes ?? ALL_UNIT_OF_TIMES),
  value,
});

const toDayOfWeek = (dayOfWeekV1: string): DayOfWeek => {
  const allDaysOfWeek = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  if (allDaysOfWeek.includes(dayOfWeekV1)) {
    return dayOfWeekV1 as DayOfWeek;
  } else {
    throw CspError.badState(`Unsupported day of week '${dayOfWeekV1}'`);
  }
};

const toRepeat = (repeatV1: Maybe<GenericRequestRepeatV1>, supportedUnitOfTimes: UnitOfTime[]): Repeat => {
  const repeatType: TimingRepeatType = getRepeatType(repeatV1);
  return {
    repeatType,
    timesOfDay: repeatV1?.timesOfDay,
    daysOfWeek: repeatV1?.daysOfWeek?.map(toDayOfWeek),
    period: repeatV1?.period,
    periodUnit: repeatV1?.periodUnit ? toUnitOfTime(repeatV1?.periodUnit, supportedUnitOfTimes) : undefined,
    frequency: repeatV1?.frequency,
    frequencyMax: repeatV1?.frequencyMax,
    duration: repeatV1?.duration,
    durationUnit: repeatV1?.durationUnit ? toUnitOfTime(repeatV1?.durationUnit, supportedUnitOfTimes) : undefined,
  };
};

type TimingMappingOptions = {
  supportedUnitOfTimes?: UnitOfTime[];
};
const toTiming = (timingV1: Maybe<GenericRequestTimingV1>, options?: TimingMappingOptions): Timing => {
  const supportedUnitOfTimes = options?.supportedUnitOfTimes ?? ALL_UNIT_OF_TIMES;
  const repeat: Repeat = toRepeat(timingV1?.repeat, supportedUnitOfTimes);

  const windowAfterV1 = timingV1?.windowAfter;
  const windowBeforeV1 = timingV1?.windowBefore;
  return ObjectUtil.removeUndefined({
    repeat,
    windowBefore: windowBeforeV1 ? toDuration(windowBeforeV1, supportedUnitOfTimes) : getDefaultWindowBefore(repeat),
    windowAfter: windowAfterV1 ? toDuration(windowAfterV1, supportedUnitOfTimes) : getDefaultWindowAfter(repeat),
  });
};

const toRequestAction = (
  action: Maybe<GenericRequestActionV1>,
  supportedUnitOfTimes: Maybe<UnitOfTime[]>,
): RequestAction => ({
  fixedAction: action?.customAction
    ? GenericRequestMapper.customActionDateToFixedAction(action?.customAction)
    : GenericRequestMapper.toFixedAction(action?.fixedAction),
  offset: action?.offset ? GenericRequestMapper.toDuration(action.offset, supportedUnitOfTimes) : undefined,
  fixedTime: action?.customAction ? GenericRequestMapper.customActionTimeToFixedTime(action.customAction) : undefined,
});

type RequestMappingOptions = {
  supportedUnitOfTimes?: UnitOfTime[];
  strict?: boolean;
};
const from = (
  scheduleCode: ScheduleCode,
  versionCode: VersionCode,
  requestV1: GenericRequestV1,
  options?: RequestMappingOptions,
): GenericRequest => {
  const startAction = toRequestAction(requestV1.startAction, options?.supportedUnitOfTimes);
  const endAction = toRequestAction(requestV1.endAction, options?.supportedUnitOfTimes);
  const requestWhile = GenericRequestMapper.toRequestWhile(requestV1.while);
  const timing = GenericRequestMapper.toTiming(requestV1.timing, options);
  const notificationConfig = ReminderNotificationMetaMapper.toNotificationConfigFromMetaV1(requestV1.meta);
  const initiationConfig = RequestInitiationConfigMapper.fromRequestInitiationMetaV2(requestV1.meta, options?.strict);
  const criticalityConfig = RequestCriticalityConfigMapper.fromRequestCriticalityMetaV1(requestV1.meta);
  const rpmEventConfig = RpmEventConfigMapper.fromRpmEventConfigMetaV1(requestV1.meta);
  const tagsConfig = RequestTagsConfigMetaMapper.fromRequestTagsConfigMetaV1(requestV1.meta);
  const groups = RequestGroupMapper.toRequestGroupMemberships(requestV1.meta, options?.strict);

  return {
    requestCode: requestV1.requestCode,
    versionCode,
    scheduleCode,
    startAction,
    endAction,
    while: requestWhile,
    timing,
    details: requestV1.details,
    notificationConfig,
    initiationConfig,
    rpmEventConfig,
    criticalityConfig,
    tagsConfig,
    groups,
  };
};

export const GenericRequestMapper = {
  customActionDateToFixedAction,
  customActionTimeToFixedTime,
  toRequestAction,
  toRequestWhile,
  toFixedAction,
  toTiming,
  toDuration,
  toRepeat,
  from,
};
